<template>
    <div class="flex flex-col" :class="stacked ? '' : 'sm:flex-row'">
        <!-- Preset rail (desktop popover only; stacked mode uses the chip row) -->
        <div
            v-if="!stacked"
            class="flex flex-col gap-0.5 p-2 sm:w-44 sm:border-r sm:border-border-base"
        >
            <button
                v-for="preset in presetOptions"
                :key="preset.value"
                type="button"
                class="w-full px-3 py-2 body-md rounded-md text-left transition-colors"
                :class="
                    pending.preset === preset.value
                        ? 'bg-bg-base text-fg-primary font-medium'
                        : 'text-fg-primary hover:bg-bg-subtle'
                "
                @click="selectPreset(preset.value)"
            >
                {{ preset.label }}
            </button>
        </div>

        <!-- Dates + calendar -->
        <div class="flex-1 p-4 flex flex-col gap-3 min-w-0">
            <div class="flex items-center gap-2">
                <input
                    :value="pendingStartText"
                    readonly
                    placeholder="Start date"
                    class="flex-1 min-w-0 rounded-md border border-border-base bg-bg-snow px-3 py-1.5 body-md text-fg-primary placeholder:text-fg-tertiary focus:outline-none focus:border-border-emphasized"
                />
                <span class="text-fg-tertiary">–</span>
                <input
                    :value="pendingEndText"
                    readonly
                    placeholder="End date"
                    class="flex-1 min-w-0 rounded-md border border-border-base bg-bg-snow px-3 py-1.5 body-md text-fg-primary placeholder:text-fg-tertiary focus:outline-none focus:border-border-emphasized"
                />
            </div>

            <!-- Preset chips (stacked/mobile sheet: horizontal scroll row
                 between the date inputs and the calendar, v2 mobile spec) -->
            <div
                v-if="stacked"
                class="flex items-center gap-2 overflow-x-auto -mx-4 px-4 pb-1"
            >
                <button
                    v-for="preset in presetOptions"
                    :key="preset.value"
                    type="button"
                    class="flex-shrink-0 px-3 py-1.5 body-md rounded-full border whitespace-nowrap transition-colors"
                    :class="
                        pending.preset === preset.value
                            ? 'bg-bg-base border-border-emphasized text-fg-primary font-medium'
                            : 'border-border-base text-fg-secondary hover:bg-bg-subtle'
                    "
                    @click="selectPreset(preset.value)"
                >
                    {{ preset.label }}
                </button>
            </div>

            <VueDatePicker
                v-if="calendarReady"
                v-model="calendarRange"
                inline
                :range="{ partialRange: false }"
                :multi-calendars="multiCalendars"
                :auto-apply="true"
                :time-config="{ enableTimePicker: false }"
                :dark="isDark"
                :max-date="new Date()"
                class="period-panel-calendar"
                @update:model-value="onCalendarChange"
            />

            <!-- Footer -->
            <div
                class="flex items-center justify-end gap-2 pt-3 border-t border-border-base"
            >
                <button
                    type="button"
                    class="px-4 py-2 body-md-500 text-fg-primary bg-bg-snow border border-border-base rounded-md hover:bg-bg-subtle transition-colors"
                    @click="$emit('cancel')"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="px-4 py-2 body-md-500 text-fg-inverted bg-bg-intense rounded-md hover:opacity-90 transition-opacity"
                    @click="apply"
                >
                    Apply
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';
import { PRESET_DEFS, PRESET_ORDER } from './period-presets';

/**
 * The v2 date-filter panel: preset rail + start/end inputs + inline range
 * calendar + Cancel/Apply. Mounted fresh on each open (the parent renders
 * it under v-if), so pending state always starts from the committed value.
 */
const props = defineProps({
    /** Committed period: { preset, start: dayjs|null, end: dayjs|null } */
    value: {
        type: Object,
        required: true,
    },
    /** Two months side by side (desktop); false = single month (mobile). */
    multiCalendars: {
        type: Boolean,
        default: true,
    },
    /** Stack the rail above the calendar (mobile sheet). */
    stacked: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['apply', 'cancel']);

// Mount the (heavy) inline calendar one frame after the popover's enter
// transition starts — mounting it synchronously inside the Transition's
// insert frame leaves the panel stuck at the enter-from opacity.
const calendarReady = ref(false);
onMounted(() => {
    requestAnimationFrame(() => {
        calendarReady.value = true;
    });
});

const presetOptions = [
    ...PRESET_ORDER.map((value) => ({
        value,
        label: PRESET_DEFS[value].label,
    })),
    { value: 'custom', label: 'Custom' },
];

// Working (uncommitted) selection.
const pending = ref({
    preset: props.value?.preset || 'all',
    start: props.value?.start || null,
    end: props.value?.end || null,
});

const isDark =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');

const pendingStartText = computed(() =>
    pending.value.start ? pending.value.start.format('MMM D, YYYY') : ''
);
const pendingEndText = computed(() =>
    pending.value.end ? pending.value.end.format('MMM D, YYYY') : ''
);

// The inline calendar works in JS Dates; keep it in sync with pending.
const calendarRange = ref(
    pending.value.start && pending.value.end
        ? [pending.value.start.toDate(), pending.value.end.toDate()]
        : null
);

const selectPreset = (value) => {
    if (value === 'custom') {
        pending.value = { ...pending.value, preset: 'custom' };
        return;
    }

    const def = PRESET_DEFS[value];
    if (!def) {
        return;
    }

    const { start, end } = def.range();
    pending.value = { preset: value, start, end };
    calendarRange.value = start && end ? [start.toDate(), end.toDate()] : null;
};

const onCalendarChange = (val) => {
    if (!val || !Array.isArray(val) || !val[0] || !val[1]) {
        return;
    }

    pending.value = {
        preset: 'custom',
        start: dayjs(val[0]),
        end: dayjs(val[1]),
    };
};

const apply = () => {
    emit('apply', { ...pending.value });
};
</script>

<style scoped>
/* Let the inline calendar size itself inside the panel. */
.period-panel-calendar :deep(.dp__menu) {
    border: none;
    box-shadow: none;
    background: transparent;
}
.period-panel-calendar :deep(.dp__instance_calendar) {
    background: transparent;
}
</style>
